import type { TFunction } from 'i18next';
import type { ImportError } from './use-file-parser-with-errors.ts';

export const normalizeEmpty = (value: unknown): unknown => (value === '' ? undefined : value);
const CSV_HEADER_LINE_COUNT = 1;
const FIRST_DATA_LINE_NUMBER = CSV_HEADER_LINE_COUNT + 1;

const MULTI_VALUE_SEPARATOR = '|';
const MULTI_VALUE_SEPARATOR_FIELDS = new Set(['responsiblesUniqueCodes']);

// Quand une erreur porte sur un champ multi-valeurs, on veut savoir lequel est en cause
export const resolveErrorValue = (
  fieldName: string | undefined,
  rawValue: unknown,
  t: TFunction
): string => {
  const value = rawValue === undefined || rawValue === null ? '' : String(rawValue);
  if (
    fieldName &&
    MULTI_VALUE_SEPARATOR_FIELDS.has(fieldName) &&
    value.includes(MULTI_VALUE_SEPARATOR)
  ) {
    return t('import.error.value.unknown');
  }
  return value;
};

export const dedupeErrors = (errors: ImportError[]): ImportError[] => {
  const unique = new Map<string, ImportError>();
  for (const err of errors) {
    const key = `${err.line ?? ''}|${err.field ?? ''}|${err.error ?? err.message}`;
    if (!unique.has(key)) unique.set(key, err);
  }
  return Array.from(unique.values());
};

export interface ViolationLike {
  property: string;
  message: string;
  detail: string;
}

export const mapViolationsToErrors = (
  violations: ViolationLike[],
  originalData: Record<string, unknown>[],
  t: TFunction
): ImportError[] => {
  const apiErrors: ImportError[] = [];
  for (const violation of violations) {
    const indexToken = violation.property.match(/\d+/)?.[0];
    const originalIndex = indexToken ? Number.parseInt(indexToken, 10) : -1;
    const lineNumber = originalIndex >= 0 ? originalIndex + FIRST_DATA_LINE_NUMBER : undefined;
    const fieldName = violation.property.split('.').pop() || undefined;
    const originalLineData =
      originalIndex >= 0
        ? (originalData[originalIndex] as Record<string, unknown> | undefined)
        : undefined;
    apiErrors.push({
      message: t('file.parser.error.line.alt', {
        line: lineNumber ?? '?',
        field: fieldName ?? '?',
        error: violation.detail || violation.message
      }),
      line: lineNumber,
      field: fieldName,
      value: fieldName ? resolveErrorValue(fieldName, originalLineData?.[fieldName], t) : undefined,
      error: violation.detail || violation.message,
      originalData: originalLineData
    });
  }
  return apiErrors;
};

export const mapViolationsToErrorsWithIndexMap = (
  violations: ViolationLike[],
  indexMap: number[],
  originalData: Record<string, unknown>[],
  t: TFunction
): ImportError[] => {
  const apiErrors: ImportError[] = [];
  for (const violation of violations) {
    const postIndexToken = violation.property.match(/\d+/)?.[0];
    const postIndex = postIndexToken ? Number.parseInt(postIndexToken, 10) : -1;
    const originalIndex = postIndex >= 0 ? (indexMap[postIndex] ?? postIndex) : -1;
    const lineNumber = originalIndex >= 0 ? originalIndex + FIRST_DATA_LINE_NUMBER : undefined;
    const fieldName = violation.property.split('.').pop() || undefined;
    const originalLineData = originalIndex >= 0 ? originalData[originalIndex] : undefined;
    apiErrors.push({
      message: t('file.parser.error.line.alt', {
        line: lineNumber ?? '?',
        field: fieldName ?? '?',
        error: violation.detail || violation.message
      }),
      line: lineNumber,
      field: fieldName,
      value: fieldName ? resolveErrorValue(fieldName, originalLineData?.[fieldName], t) : undefined,
      error: violation.detail || violation.message,
      originalData: originalLineData
    });
  }
  return apiErrors;
};
